import React, { useEffect, useRef } from "react"
import PropTypes from "prop-types"

/*
 * The Euroweb mark: eight isometric cubes, gold on top and blue on the sides,
 * laid out as a 3×3 pixel "E". Geometry is computed once and shared by the
 * static logo and the animated hero version.
 */

const COS30 = Math.cos(Math.PI / 6)
const UNIT = 20
// Rows run along the tile's upper-right edge (the top of the letter) down to
// its lower-left edge; the gap in the middle row opens the E to the right.
const GLYPH = ["###", "##.", "###"]
const SIZE = GLYPH.length
const PITCH = 1.22
const HEIGHT = 1

// Hero motion. Heights are in cube units, times in milliseconds.
const MAX_LIFT = 1.3
const MAX_PULL = 0.8
const PULL_SIGMA = 1.05 * UNIT
const PULL_EASE_MS = 260
const INTRO_MS = 2000
const DRIFT_HEIGHT = 0.2
const DRIFT_FADE_IN_MS = 3000
const EVENT_GAP_MS = [2200, 4200]
const FLOAT_HEIGHT = [0.5, 0.85]
const FLOAT_MS = [2600, 3600]
const WAVE_HEIGHT = 0.42
const WAVE_MS = 2400
const WAVE_SPREAD_MS = 1800

const project = (x, y, z) => [(x - y) * COS30 * UNIT, ((x + y) / 2 - z) * UNIT]

const toPoints = corners =>
  corners
    .map(corner =>
      project(...corner)
        .map(n => n.toFixed(2))
        .join(",")
    )
    .join(" ")

const CUBES = []
for (let j = 0; j < SIZE; j++) {
  for (let i = 0; i < SIZE; i++) {
    if (GLYPH[j][i] !== "#") continue
    const [x0, y0] = [i * PITCH, j * PITCH]
    const [x1, y1] = [x0 + 1, y0 + 1]
    const h = HEIGHT
    const last = SIZE - 1
    CUBES.push({
      id: `${i}-${j}`,
      col: i,
      row: j,
      depth: i + j,
      top: toPoints([[x0, y0, h], [x1, y0, h], [x1, y1, h], [x0, y1, h]]),
      front: toPoints([[x0, y1, 0], [x1, y1, 0], [x1, y1, h], [x0, y1, h]]),
      side: toPoints([[x1, y0, 0], [x1, y1, 0], [x1, y1, h], [x1, y0, h]]),
      base: toPoints([[x0, y0, 0], [x1, y0, 0], [x1, y1, 0], [x0, y1, 0]]),
      center: project(x0 + 0.5, y0 + 0.5, h),
      // Where the cube sits (0–1) along each direction a hero wave can travel.
      phases: [
        (i + j) / (2 * last), // back to front
        1 - (i + j) / (2 * last), // front to back
        (i - j + last) / (2 * last), // left to right
        1 - (i - j + last) / (2 * last), // right to left
        Math.hypot(i - last / 2, j - last / 2) / Math.hypot(last / 2, last / 2), // centre out
        1 - Math.hypot(i - last / 2, j - last / 2) / Math.hypot(last / 2, last / 2), // edges in
      ],
    })
  }
}
// Painter's order: cubes further from the viewer are drawn first.
CUBES.sort((a, b) => a.depth - b.depth)

const extent = (SIZE - 1) * PITCH + 1
const bounds = {
  minX: -extent * COS30 * UNIT,
  maxX: extent * COS30 * UNIT,
  minY: -HEIGHT * UNIT,
  maxY: extent * UNIT,
}

const viewBox = (pad, headroom = 0) =>
  [
    bounds.minX - pad,
    bounds.minY - pad - headroom,
    bounds.maxX - bounds.minX + pad * 2,
    bounds.maxY - bounds.minY + pad * 2 + headroom,
  ]
    .map(n => n.toFixed(2))
    .join(" ")

const CubeFaces = ({ cube }) => (
  <>
    <polygon className="mark__top" points={cube.top} />
    <polygon className="mark__front" points={cube.front} />
    <polygon className="mark__side" points={cube.side} />
  </>
)

CubeFaces.propTypes = {
  cube: PropTypes.object.isRequired,
}

// How far a floating cube hovers above its slot, in cube units.
const FLOAT_LIFT = 1.5
const FLOAT_HEADROOM = (FLOAT_LIFT + 0.3) * UNIT

export const Mark = ({ className, title, floating }) => (
  <svg
    className={`mark ${className}`}
    viewBox={viewBox(1, floating.length ? FLOAT_HEADROOM : 0)}
    role={title ? "img" : undefined}
    aria-label={title || undefined}
    aria-hidden={title ? undefined : true}
    focusable="false"
  >
    {CUBES.map(cube =>
      floating.includes(cube.id) ? (
        <g key={cube.id}>
          <g className="mark__ghost">
            <polygon points={cube.top} />
            <polygon points={cube.front} />
            <polygon points={cube.side} />
          </g>
          <g className="mark__float" style={{ "--float": `${-FLOAT_LIFT * UNIT}px` }}>
            <CubeFaces cube={cube} />
          </g>
        </g>
      ) : (
        <CubeFaces key={cube.id} cube={cube} />
      )
    )}
  </svg>
)

Mark.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  floating: PropTypes.arrayOf(PropTypes.string),
}

Mark.defaultProps = {
  className: "",
  title: "",
  floating: [],
}

// The cube that has wandered off on the 404 page: the back corner, so it
// hovers in open space and the E stays readable.
export const STRAY_CUBE = "0-0"

const TAU = Math.PI * 2
const random = (min, max) => min + Math.random() * (max - min)
const smootherstep = t => t * t * t * (t * (t * 6 - 15) + 10)

// Float up, hover for a moment, then settle back down.
const envelope = x => {
  if (x <= 0 || x >= 1) return 0
  if (x < 0.4) return smootherstep(x / 0.4)
  if (x < 0.55) return 1
  return smootherstep((1 - x) / 0.45)
}

const neighbours = index =>
  CUBES.flatMap((cube, i) =>
    Math.abs(cube.col - CUBES[index].col) + Math.abs(cube.row - CUBES[index].row) === 1 ? [i] : []
  )

// One random moment: a cube (sometimes with a neighbour or two) floats up, or
// a slow wave crosses the tile in a random direction.
const randomEvent = start => {
  if (Math.random() < 0.6) {
    const first = Math.floor(Math.random() * CUBES.length)
    const group = [first, ...neighbours(first).filter(() => Math.random() < 0.3)]
    const delays = CUBES.map((_, i) => (i === first ? 0 : group.includes(i) ? random(250, 600) : null))
    return { start, delays, height: random(...FLOAT_HEIGHT), duration: random(...FLOAT_MS) }
  }
  const pattern = Math.floor(Math.random() * CUBES[0].phases.length)
  const delays = CUBES.map(cube => cube.phases[pattern] * WAVE_SPREAD_MS)
  return { start, delays, height: WAVE_HEIGHT, duration: WAVE_MS }
}

const eventLift = (event, i, now) =>
  event.delays[i] === null
    ? 0
    : event.height * envelope((now - event.start - event.delays[i]) / event.duration)

const eventEnd = event => event.start + Math.max(...event.delays.filter(d => d !== null)) + event.duration

export const HeroMark = () => {
  const svgRef = useRef(null)
  const liftRefs = useRef([])

  useEffect(() => {
    const svg = svgRef.current
    const area = svg && svg.parentElement
    if (!area) return undefined
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined

    // The CSS drop-in plays first; idle motion fades in once it has settled.
    const idleStart = performance.now() + INTRO_MS
    // Each cube drifts on its own pair of slow, unrelated sine waves.
    const drift = CUBES.map(() => ({
      periods: [random(5200, 9000), random(3100, 5600)],
      offsets: [random(0, TAU), random(0, TAU)],
    }))
    const pullTarget = CUBES.map(() => 0)
    const pull = CUBES.map(() => 0)
    let events = []
    let nextEventAt = idleStart + 800
    let lastFrame = 0
    let frame = 0
    let running = false

    const render = now => {
      const dt = Math.min(64, now - (lastFrame || now))
      lastFrame = now
      const follow = 1 - Math.exp(-dt / PULL_EASE_MS)

      if (now >= nextEventAt) {
        events.push(randomEvent(now))
        nextEventAt = now + random(...EVENT_GAP_MS)
      }
      events = events.filter(event => eventEnd(event) > now)

      const driftGain = smootherstep(Math.min(1, Math.max(0, (now - idleStart) / DRIFT_FADE_IN_MS)))

      CUBES.forEach((cube, i) => {
        pull[i] += (pullTarget[i] - pull[i]) * follow
        const { periods, offsets } = drift[i]
        const wander =
          0.5 +
          0.3 * Math.sin((TAU * now) / periods[0] + offsets[0]) +
          0.2 * Math.sin((TAU * now) / periods[1] + offsets[1])
        const float = events.reduce((highest, event) => Math.max(highest, eventLift(event, i, now)), 0)
        const lift = Math.min(MAX_LIFT, driftGain * DRIFT_HEIGHT * wander + float + pull[i])
        const el = liftRefs.current[i]
        if (el) el.style.transform = lift > 0.002 ? `translateY(${(-lift * UNIT).toFixed(2)}px)` : ""
      })
      frame = requestAnimationFrame(render)
    }

    const start = () => {
      if (running) return
      running = true
      lastFrame = 0
      // Don't fire a backlog of moments after the hero was off screen.
      nextEventAt = Math.max(nextEventAt, performance.now() + 600)
      frame = requestAnimationFrame(render)
    }
    const stop = () => {
      running = false
      cancelAnimationFrame(frame)
    }

    // Only animate while the hero is on screen.
    const observer = new IntersectionObserver(([entry]) =>
      entry.isIntersecting ? start() : stop()
    )
    observer.observe(svg)

    const onMove = event => {
      const ctm = svg.getScreenCTM()
      if (!ctm) return
      const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(ctm.inverse())
      CUBES.forEach((cube, i) => {
        const dx = point.x - cube.center[0]
        const dy = point.y - cube.center[1]
        pullTarget[i] = MAX_PULL * Math.exp(-(dx * dx + dy * dy) / (2 * PULL_SIGMA * PULL_SIGMA))
      })
    }
    const onLeave = () => pullTarget.fill(0)

    area.addEventListener("pointermove", onMove)
    area.addEventListener("pointerleave", onLeave)
    return () => {
      stop()
      observer.disconnect()
      area.removeEventListener("pointermove", onMove)
      area.removeEventListener("pointerleave", onLeave)
    }
  }, [])

  return (
    <svg
      ref={svgRef}
      className="mark mark--hero"
      viewBox={viewBox(UNIT * 0.4, MAX_LIFT * UNIT)}
      role="img"
      aria-label="Euroweb logo: eight cubes forming the letter E"
    >
      {CUBES.map(cube => (
        <polygon key={`base-${cube.id}`} className="mark__socket" points={cube.base} />
      ))}
      {CUBES.map((cube, i) => (
        <g key={cube.id} className="mark__drop" style={{ "--wave": cube.depth }}>
          <g
            ref={el => {
              liftRefs.current[i] = el
            }}
          >
            <CubeFaces cube={cube} />
          </g>
        </g>
      ))}
    </svg>
  )
}
